// the read-modify-write behind a starred report. Starred reports share the per-user ui-preference
// bucket with the customer-list column preset, and PUT replaces that bucket whole.

export type PreferenceBucket = Record<string, unknown>

/** Namespaced key inside the shared bucket. */
export const STARRED_REPORTS_KEY = 'starred_reports'

/** Everything the caller must supply. Keeps this module free of store and http. */
export interface StarredReportsPort {
  /** False before a merchant is loaded: update the sidebar, save nothing. */
  canPersist(): boolean
  read(): Promise<PreferenceBucket>
  write(bucket: PreferenceBucket): Promise<PreferenceBucket>
  /** The list the sidebar shows right now. */
  current(): string[]
  setStarred(keys: string[]): void
  setBucket(bucket: PreferenceBucket): void
  onLoadError(error: unknown): void
  onSaveError(error: unknown): void
  /**
   * Runs a read-modify-write with exclusive access to the bucket. The chain in
   * this module orders starred operations against each other; this orders them
   * against the customer list, which owns another key in the same bucket.
   */
  exclusive<T>(task: () => Promise<T>): Promise<T>
}

export function starredFromBucket(bucket?: PreferenceBucket | null): string[] {
  const keys = bucket?.[STARRED_REPORTS_KEY]

  return Array.isArray(keys) ? (keys as string[]) : []
}

export function toggleKey(keys: string[], key: string): string[] {
  return keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key]
}

/**
 * Undo one toggle against the list as it stands now, rather than restoring a
 * snapshot taken before the write. A star the user clicked while the failed
 * write was in flight then survives the revert.
 */
export function undoToggle(
  keys: string[],
  key: string,
  wasStarred: boolean
): string[] {
  if (wasStarred) {
    return keys.includes(key) ? keys : [...keys, key]
  }

  return keys.filter(k => k !== key)
}

let writeChain: Promise<void> = Promise.resolve()
// Bumped by each local toggle, so a queued load can tell the user has moved on
// since it went out and its server list is the older of the two.
let localToggles = 0

/** Test seam: the chain is module state, so a test must be able to clear it. */
export function resetStarredWriteChain() {
  writeChain = Promise.resolve()
  localToggles = 0
}

/**
 * Fill the sidebar pills from the stored bucket. Queued on the same chain as the
 * writes: a load that overtakes one reads the pre-click bucket, and the write
 * then sends that stale list. A star clicked after this load was enqueued is
 * newer than anything the GET can return, so the list is left alone — the write
 * queued behind it will send it.
 */
export function loadStarredReports(port: StarredReportsPort): Promise<void> {
  if (!port.canPersist()) return Promise.resolve()

  const togglesAtEnqueue = localToggles
  const isCurrent = () => localToggles === togglesAtEnqueue

  writeChain = writeChain.then(async () => {
    try {
      const bucket = await port.read()
      port.setBucket(bucket)
      if (isCurrent()) port.setStarred(starredFromBucket(bucket))
    } catch (error) {
      // The port maps a missing bucket to {}, so a failure here is a real one.
      // Leave the bucket alone: the customer-list column preset reads the same
      // store key, and a transient 500 must not look like "no preset saved".
      if (isCurrent()) port.setStarred([])
      port.onLoadError(error)
    }
  })

  return writeChain
}

/**
 * Star or unstar one report. The pill appears or goes at once, because starring
 * happens while the user reads the flyout and must not wait on the network.
 */
export function toggleStarredReport(
  port: StarredReportsPort,
  reportKey: string
): Promise<void> {
  const before = port.current()
  const wasStarred = before.includes(reportKey)

  port.setStarred(toggleKey(before, reportKey))
  localToggles += 1

  if (!port.canPersist()) return Promise.resolve()

  writeChain = writeChain.then(async () => {
    try {
      await port.exclusive(async () => {
        const bucket = await port.read()
        // Send what the sidebar now shows, not the list captured at click time,
        // so a queued write cannot undo a later click.
        const merged = { ...bucket, [STARRED_REPORTS_KEY]: port.current() }
        port.setBucket(await port.write(merged))
      })
    } catch (error) {
      port.setStarred(undoToggle(port.current(), reportKey, wasStarred))
      port.onSaveError(error)
    }
  })

  return writeChain
}
